import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { aboutContent } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

// Background images for each benefit - matching original site style (upscaled for quality)
const benefitBackgrounds = [
  '/images/slide01-power-plant.png', // Power plant/station - Jobs
  '/images/slide02-emissions.png', // Aerial industrial with gases - Emissions
  '/images/slide03-graphite.png', // Graphite material - Supply chains
  'https://images.pexels.com/photos/27243718/pexels-photo-27243718.jpeg', // EV charging station - Consumer savings
];

const About = () => {
  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomKey, setZoomKey] = useState(0); // Key to trigger re-animation

  useEffect(() => {
    const section = sectionRef.current;
    const numBenefits = aboutContent.benefits.length;
    
    // Each benefit gets 100vh of scroll distance
    const scrollDistancePerBenefit = window.innerHeight;
    const totalScrollDistance = scrollDistancePerBenefit * numBenefits;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: `+=${totalScrollDistance}px`,
        pin: true,
        pinSpacing: true,
        scrub: 0.3,
        onUpdate: (self) => {
          const progress = self.progress;
          
          // Calculate active index - divide progress into equal segments
          const segmentSize = 1 / numBenefits;
          const newIndex = Math.min(
            Math.floor(progress / segmentSize),
            numBenefits - 1
          );
          
          // Only update if index changed
          if (newIndex !== activeIndex) {
            setActiveIndex(newIndex);
            setZoomKey(prev => prev + 1); // Trigger new zoom animation
          }
        },
      });
    }, section);

    return () => ctx.revert();
  }, [activeIndex]);

  return (
    <section ref={sectionRef} className="relative h-screen overflow-hidden">
      {/* Add CSS for zoom animation with enhanced quality */}
      <style>{`
        @keyframes zoomOut {
          0% {
            transform: scale(1.08);
          }
          100% {
            transform: scale(1.0);
          }
        }
        .zoom-animate {
          animation: zoomOut 4s ease-out forwards;
        }
        .high-quality-img {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }
      `}</style>

      {/* Background Images with crossfade and auto zoom-out effect */}
      <div className="absolute inset-0">
        {benefitBackgrounds.map((bg, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              activeIndex === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              key={activeIndex === index ? `${index}-${zoomKey}` : index}
              src={bg}
              alt=""
              className={`w-full h-full object-cover high-quality-img ${
                activeIndex === index ? 'zoom-animate' : ''
              }`}
              style={{
                transform: activeIndex === index ? undefined : 'scale(1.08)',
                willChange: 'transform, opacity',
              }}
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="l-container w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Left Side - Main Text */}
            <div>
              <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl text-white leading-tight mb-8">
                {aboutContent.subtitle}
              </h2>
              <Link 
                to={aboutContent.ctaLink} 
                className="inline-flex items-center gap-3 text-white text-sm font-medium hover:text-[#4ECDC4] transition-colors group"
              >
                {aboutContent.cta}
                <span className="w-8 h-[1px] bg-current group-hover:w-12 transition-all duration-300" />
              </Link>
            </div>

            {/* Right Side - Benefits List */}
            <div className="space-y-8 lg:space-y-10">
              {aboutContent.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`transition-all duration-500 ${
                    activeIndex === index 
                      ? 'opacity-100' 
                      : 'opacity-30'
                  }`}
                >
                  <h4 className="font-serif text-xl lg:text-2xl text-white mb-2">
                    {benefit.title}
                  </h4>
                  <p className="text-white/80 text-sm lg:text-base leading-relaxed">
                    {benefit.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Progress Indicator - Dots */}
      <div className="absolute right-8 lg:right-12 top-1/2 -translate-y-1/2 flex flex-col gap-3">
        {aboutContent.benefits.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeIndex === index 
                ? 'bg-white scale-125' 
                : 'bg-white/40'
            }`}
          />
        ))}
      </div>

      {/* Active Indicator Label */}
      <div className="absolute left-8 lg:left-12 bottom-8 lg:bottom-12">
        <span className="text-white/60 text-sm">
          {String(activeIndex + 1).padStart(2, '0')} / {String(aboutContent.benefits.length).padStart(2, '0')}
        </span>
      </div>
    </section>
  );
};

export default About;
